/**
 * Sprint 31 M1 — Policy Library row actions.
 *
 * Operator-facing actions on a Policy Library row. The Library is the
 * operational control center; operators must be able to enable, disable,
 * duplicate, or delete a policy without leaving it.
 *
 * Execution boundary: `policy_registry` is the operator INDEX, not the
 * execution source of truth. Mutations are dispatched to the capability
 * table named in `canonical_targets[0]`; the existing capability sync
 * triggers update the registry. Capabilities that are not yet migrated
 * (everything outside hold_release / hold_creation) update the registry
 * directly as a degraded mode — they still need their canonical writer
 * for runtime effect, and that's tracked in the capability status ledger.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const PolicyIdInput = z.object({ policyId: z.string().min(1) });

type Target = { capability: string; table: string; id: string };

async function loadRegistry(supabase: any, policyId: string) {
  const isUuid = /^[0-9a-f-]{36}$/i.test(policyId);
  const col = isUuid ? "id" : "policy_id";
  const { data, error } = await supabase
    .from("policy_registry")
    .select("id, tenant_id, policy_id, title, primary_capability, canonical_targets, status, customer_id, scope_level, intent_id, source")
    .eq(col, policyId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("policy not found");
  const targets: Target[] = Array.isArray(data.canonical_targets) ? data.canonical_targets : [];
  const primary = targets.find((t) => t.capability === data.primary_capability) ?? targets[0] ?? null;
  return { reg: data, primary };
}

/**
 * Set status — Active / Paused / Archived.
 *
 * For wired capabilities (hold_release, hold_creation) this updates the
 * canonical row's `is_active`, and the existing sync trigger flips the
 * registry between active/paused. Archived is written to the registry
 * directly (capability tables have no archived state today).
 */
export const setPolicyStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        policyId: z.string().min(1),
        status: z.enum(["active", "paused", "archived"]),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any;
    const { reg, primary } = await loadRegistry(supabase, data.policyId);

    if (data.status === "archived") {
      // Archive is a registry-only concept today. Capability row stays
      // intact so execution semantics don't shift; the registry hides it.
      const { error } = await supabase
        .from("policy_registry")
        .update({ status: "archived" })
        .eq("id", reg.id);
      if (error) throw new Error(error.message);
      return { ok: true };
    }

    const wantActive = data.status === "active";
    if (primary && (primary.table === "hold_release_rules" || primary.table === "hold_creation_rules")) {
      const { error } = await supabase
        .from(primary.table)
        .update({ is_active: wantActive })
        .eq("id", primary.id);
      if (error) throw new Error(error.message);
      return { ok: true };
    }

    // Fallback: registry only (capability not yet wired for row actions).
    const { error } = await supabase
      .from("policy_registry")
      .update({ status: data.status })
      .eq("id", reg.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/**
 * Duplicate — clone the capability row; the sync trigger creates a new
 * registry row with a fresh `POL-YYYY-NNNN`.
 */
export const duplicatePolicy = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => PolicyIdInput.parse(i))
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any;
    const { reg, primary } = await loadRegistry(supabase, data.policyId);
    if (!primary) throw new Error("Cannot duplicate: no canonical target.");

    if (primary.table === "hold_release_rules") {
      const { data: src, error } = await supabase
        .from("hold_release_rules")
        .select("tenant_id, hold_reason, trigger, params, is_active, scope_level, customer_id, intent_id, intent_summary, intent_prompt, source")
        .eq("id", primary.id)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!src) throw new Error("Source policy not found.");
      const { error: insErr } = await supabase.from("hold_release_rules").insert({
        ...src,
        is_active: false,
        source: "manual",
        intent_id: null,
        intent_prompt: null,
        intent_summary: src.intent_summary ? `${src.intent_summary} (Copy)` : null,
      });
      if (insErr) {
        // (tenant_id, scope, hold_reason) is unique — only one release rule
        // per reason per scope. Surface a friendly message.
        if (/duplicate key|unique/i.test(insErr.message)) {
          throw new Error(
            "A Hold Release policy already exists for this hold reason in this scope. Edit the existing policy or create one for a different scope.",
          );
        }
        throw new Error(insErr.message);
      }
      return { ok: true };
    }

    if (primary.table === "hold_creation_rules") {
      // Phase 3 dual-write: RPC does the insert AND mirrors into public.policies.
      const { error: rpcErr } = await supabase.rpc(
        "hold_creation_rule_duplicate_v1",
        { _id: primary.id },
      );
      if (rpcErr) throw new Error(rpcErr.message);
      return { ok: true };
    }

    throw new Error(`Duplicate not yet supported for capability '${reg.primary_capability}'. Open the policy and use the capability's manual editor.`);
  });

/**
 * Delete — removes the canonical capability row; the sync trigger removes
 * the registry index. Audit history (`audit_log`) is preserved separately.
 * For capabilities not yet wired, archives the registry entry instead.
 */
export const deletePolicy = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => PolicyIdInput.parse(i))
  .handler(async ({ data, context }) => {
    const supabase = context.supabase as any;
    const { reg, primary } = await loadRegistry(supabase, data.policyId);

    if (primary && (primary.table === "hold_release_rules" || primary.table === "hold_creation_rules")) {
      if (primary.table === "hold_creation_rules") {
        // Phase 3 dual-write: RPC deletes legacy + mirror in one txn.
        const { error } = await supabase.rpc("hold_creation_rule_delete_v1", {
          _id: primary.id,
        });
        if (error) throw new Error(error.message);
      } else {
        const { error } = await supabase.from(primary.table).delete().eq("id", primary.id);
        if (error) throw new Error(error.message);
      }
      return { ok: true };
    }
    // Fallback: soft archive on the registry (capability writer not wired).
    const { error } = await supabase
      .from("policy_registry")
      .update({ status: "archived" })
      .eq("id", reg.id);
    if (error) throw new Error(error.message);
    return { ok: true, archivedOnly: true };
  });